import { useState } from "react";
import { categories, todayMelons, listMelons } from "@/mocks/melonData";
import { statusTone, categoryIcon, categoryTone } from "./melonUi";

function statusLabel(status: string): string {
  if (status === "确认瓜") return "有公开回应 · 关键信息对齐";
  if (status === "落地瓜") return "处理已落地 · 事态收尾";
  return "多方一致 · 结论不再猜测";
}

export function TodaySection() {
  return (
    <section id="today" className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent-600">
            Daily Verified
          </p>
          <h2 className="mt-2 font-heading text-3xl text-foreground-950 md:text-4xl">
            今日已实锤合集
          </h2>
        </div>
        <div className="flex items-center gap-2 text-xs font-medium text-foreground-600">
          <span className="flex h-2 w-2 rounded-full bg-accent-500"></span>
          2026.09.07 · 已收录 26 条，以下为关注度较高的 8 条
        </div>
      </div>

      <div className="mt-8 divide-y divide-background-200 border-y border-background-200 bg-background-50">
        {todayMelons.map((m, i) => (
          <article
            key={m.title}
            className="group grid grid-cols-1 gap-3 px-1 py-6 transition-colors hover:bg-background-100/60 sm:grid-cols-[64px_1fr_auto] sm:items-start sm:gap-5"
          >
            <span className="font-heading text-3xl text-background-300 transition-colors group-hover:text-primary-300 sm:text-4xl">
              {String(i + 1).padStart(2, "0")}
            </span>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className={`inline-flex items-center gap-1 text-xs font-semibold ${categoryTone(m.category)}`}>
                  <i className={categoryIcon(m.category)}></i>
                  {m.category}
                </span>
                <span className="inline-flex items-center gap-1 text-xs text-foreground-500">
                  <i className="ri-time-line"></i>
                  {m.time}
                </span>
              </div>
              <h3 className="mt-2 text-base font-semibold leading-snug text-foreground-950 md:text-lg">
                {m.title}
              </h3>
              <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{m.summary}</p>
              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-foreground-500">
                <span className="inline-flex items-center gap-1">
                  <i className="ri-file-shield-2-line"></i>
                  信源：{m.source}
                </span>
                <span className="inline-flex items-center gap-1">
                  <i className="ri-fire-line"></i>
                  {m.hot} 热度
                </span>
              </div>
            </div>
            <div className="flex flex-row items-center gap-2 sm:w-40 sm:flex-col sm:items-end sm:gap-1.5">
              <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1 text-xs font-bold ${statusTone(m.status)}`}>
                {m.status}
              </span>
              <span className="hidden text-[11px] text-foreground-500 sm:inline">
                {statusLabel(m.status)}
              </span>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

export function VerifyListSection() {
  const all = [...todayMelons, ...listMelons];
  const [active, setActive] = useState<string>("全部");

  const filtered =
    active === "全部" ? all : all.filter((m) => m.category === active);

  return (
    <section id="list" className="bg-background-100/70">
      <div className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary-500">
              Confirmed List
            </p>
            <h2 className="mt-2 font-heading text-3xl text-foreground-950 md:text-4xl">
              实锤名单
            </h2>
            <p className="mt-2 max-w-xl text-sm leading-relaxed text-foreground-600">
              确认瓜、落地瓜与盖棺定论逐条列明，附信源与热度。下方可用栏目切换，只看你想看的圈层。
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2 rounded-full border border-background-200 bg-background-50 p-1.5 text-xs">
            <span className="inline-flex items-center gap-1.5 px-2">
              <span className="h-2 w-2 rounded-full bg-primary-500"></span>确认瓜
            </span>
            <span className="inline-flex items-center gap-1.5 px-2">
              <span className="h-2 w-2 rounded-full bg-accent-500"></span>落地瓜
            </span>
            <span className="inline-flex items-center gap-1.5 px-2">
              <span className="h-2 w-2 rounded-full bg-secondary-600"></span>盖棺定论
            </span>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setActive("全部")}
            className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
              active === "全部"
                ? "bg-primary-500 text-background-50"
                : "bg-background-50 text-foreground-600 hover:bg-background-50 hover:text-primary-700"
            }`}
          >
            全部 · {all.length}
          </button>
          {categories.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => setActive(c.name)}
              className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                active === c.name
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-50 text-foreground-600 hover:bg-background-50 hover:text-primary-700"
              }`}
            >
              <i className={c.icon}></i>
              {c.name}
            </button>
          ))}
        </div>

        <div className="mt-6 overflow-hidden rounded-xl border border-background-200 bg-background-50">
          {filtered.length === 0 ? (
            <div className="px-6 py-14 text-center">
              <p className="text-sm text-foreground-600">
                该栏目当前暂无达到「已实锤」标准的内容，回到其他栏目看看吧。
              </p>
            </div>
          ) : (
            filtered.map((m, idx) => (
              <article
                key={m.title}
                className="grid grid-cols-1 gap-3 border-b border-background-200 px-5 py-5 transition-colors last:border-b-0 hover:bg-background-100/50 md:grid-cols-[52px_1fr_auto] md:items-center md:gap-5"
              >
                <span className="text-sm font-bold text-background-400">
                  {String(idx + 1).padStart(2, "0")}
                </span>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span className={`inline-flex items-center gap-1 text-xs font-semibold ${categoryTone(m.category)}`}>
                      <i className={categoryIcon(m.category)}></i>
                      {m.category}
                    </span>
                    <span className="inline-flex items-center gap-1 text-xs text-foreground-500">
                      <i className="ri-file-shield-2-line"></i>
                      {m.source}
                    </span>
                  </div>
                  <h3 className="mt-1 text-sm font-semibold text-foreground-950 md:text-base">
                    {m.title}
                  </h3>
                  <p className="mt-0.5 hidden text-xs leading-relaxed text-foreground-500 sm:block">
                    {m.summary}
                  </p>
                </div>
                <span
                  className={`inline-flex items-center justify-center gap-1 whitespace-nowrap rounded-full px-3 py-1 text-xs font-bold md:justify-start ${statusTone(m.status)}`}
                >
                  {m.status}
                </span>
              </article>
            ))
          )}
        </div>
      </div>
    </section>
  );
}